import React, { FC, useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { ZodError } from 'zod'

import { ImagePayload } from '../../nodes/ImageNode/ImageNode'
import { getInsertImageSchema, FormValidationError } from './validation'
import { FormField } from './FormField'
import { useCustomTranslation } from '../../../../Hooks/useCustomTranslation'
import { Dialog } from '../../../Dialog'
import { Upload } from '../../../FileTransfer'
import { Typography } from '../../../Typography'
import { NotificationImageUploadResponse } from '@e3dc-react-apps/portal/src/Libs/notifications/models/notifications.model'

interface InsertImageDialogProps {
  open: boolean
  onClose: () => void
  onSave: (formData: ImagePayload) => void
  initialFormData?: ImagePayload
  onImageDropped?: (image: File | undefined) => Promise<NotificationImageUploadResponse | undefined>
  isLoading?: boolean
}

const useStyles = makeStyles()(theme => ({
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: `${theme.spacing(4)} 0 !important`,
    gap: theme.spacing(2),
  },
}))

const emptyFormData = {
  src: '',
  altText: '',
  width: '',
  height: '',
}

/**
 * Dialog for inserting/updating images in the markdown editor
 */
export const ImageDialog: FC<InsertImageDialogProps> = ({ open, onClose, onSave, initialFormData, onImageDropped, isLoading }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const [formData, setFormData] = useState<ImagePayload>(initialFormData || emptyFormData)
  const { height, width, altText, src } = formData
  const [validationError, setValidationError] = useState<FormValidationError>(null)

  const insertImageSchema = useMemo(() => getInsertImageSchema(t), [t])

  const onChangeFormData = (value: string, key: keyof ImagePayload): void => {
    setFormData(oldData => ({
      ...oldData,
      [key]: value,
    }))
  }

  const getValidationErrors = async (): Promise<FormValidationError> => {
    let error: FormValidationError = null

    try {
      await insertImageSchema.parseAsync(formData)
    } catch (err) {
      error = err as ZodError
    }

    return error
  }

  const onClick = async (): Promise<void> => {
    const error = await getValidationErrors()

    if (error) {
      setValidationError(error)
      return
    } else {
      setValidationError(null)
    }

    onSave(formData)
    onClose()
  }

  useEffect(() => {
    setFormData(initialFormData || emptyFormData)
    setValidationError(null)
  }, [open, initialFormData])

  const handleFileDrop = (file: File | undefined): void => {
    onImageDropped?.(file).then(data => {
      if (data?.url) {
        onChangeFormData(data.url, 'src')
      }
    })
  }

  return (
    <Dialog
      onClose={onClose}
      maxWidth="sm"
      title={initialFormData ? t('shell.markdown-editor.edit-image') : t('shell.markdown-editor.insert-image')}
      actions={{
        primary: {
          name: initialFormData ? t('shell.markdown-editor.update') : t('shell.markdown-editor.insert'),
          onClick: onClick,
          isLoading,
        },
        secondary: {
          name: t('shell.markdown-editor.cancel'),
        },
      }}
    >
      <Box className={classes.container}>
        <FormField
          label={t('shell.markdown-editor.image-url')}
          placeholder="i.e. https://source.unsplash.com/random"
          onChange={onChangeFormData}
          value={src}
          error={validationError}
          fieldKey={'src'}
        />
        {onImageDropped && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={theme => ({ height: 0, borderTop: `1px solid ${theme.palette.shades.shade02}`, width: '100%' })} />
              <Typography>{'shell.markdown-editor.or'}</Typography>
              <Box sx={theme => ({ height: 0, borderTop: `1px solid ${theme.palette.shades.shade02}`, width: '100%' })} />
            </Box>
            <Upload onFilesDropped={handleFileDrop} />
          </>
        )}
        <FormField
          label={t('shell.markdown-editor.width')}
          placeholder={t('shell.markdown-editor.enter-width')}
          onChange={onChangeFormData}
          value={width}
          error={validationError}
          fieldKey={'width'}
          type={'number'}
        />
        <FormField
          label={t('shell.markdown-editor.height')}
          placeholder={t('shell.markdown-editor.enter-height')}
          onChange={onChangeFormData}
          value={height}
          error={validationError}
          fieldKey={'height'}
          type={'number'}
        />
        <FormField
          label={t('shell.markdown-editor.alt-text')}
          placeholder={t('shell.markdown-editor.alt-text')}
          onChange={onChangeFormData}
          value={altText}
          error={validationError}
          fieldKey={'altText'}
        />
      </Box>
    </Dialog>
  )
}
